// recova GmbH – Dashboard-Rahmen: Header mit Logo, Backoffice-Sektions-Navigation, Tabs.
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Recycle, LogOut } from "lucide-react";
import { toast } from "sonner";

export const BACKOFFICE_SECTIONS = [
  { label: "Aufträge & Disposition", to: "/app/office" },
  { label: "Lager & Ersatzteile", to: "/app/lager" },
  { label: "System & Backups", to: "/app/admin" },
];

export const DashboardLayout = ({ title, subtitle, sections = [], nav = [], active, onNav, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => {
    await logout();
    toast.success("Abgemeldet");
    navigate("/login");
  };
  return (
    <div className="min-h-screen bg-[#f1f5f9]">
      <header className="glass-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="recova Logo" className="h-9 w-9 rounded-full object-cover" data-testid="logo" />
            <div>
              <p className="font-heading text-lg font-extrabold leading-tight text-[#1b4332]">recova</p>
              <p className="text-xs text-slate-500">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-600 sm:block" data-testid="header-user-name">{user?.name}</span>
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="logout-button">
              <LogOut className="mr-1 h-4 w-4" /> Abmelden
            </Button>
          </div>
        </div>
        {sections.length > 0 && (
          <div className="mx-auto flex max-w-7xl gap-1 border-t border-slate-100 px-4 pb-2 pt-1.5 sm:px-6" data-testid="backoffice-sections">
            {sections.map((s) => (
              <button key={s.to} onClick={() => navigate(s.to)} data-testid={`section-link-${s.to.replace(/\//g, "-")}`}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${s.active ? "bg-[#028090] text-white" : "text-slate-500 hover:bg-slate-100 hover:text-[#1b4332]"}`}>
                {s.label}
              </button>
            ))}
          </div>
        )}
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <h1 className="font-heading mb-1 text-2xl font-bold tracking-tight text-[#0f172a] sm:text-3xl" data-testid="dashboard-title">{title}</h1>
        {nav.length > 0 && (
          <div className="mb-6 mt-4 flex flex-wrap gap-2">
            {nav.map((n) => (
              <button
                key={n.key}
                data-testid={`nav-${n.key}`}
                onClick={() => onNav(n.key)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                  active === n.key
                    ? "bg-[#1b4332] text-white shadow-sm"
                    : "bg-white text-slate-600 border border-slate-200 hover:bg-[#e8f0ec] hover:text-[#1b4332]"
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  {n.icon}
                  {n.label}
                  {n.badge > 0 && (
                    <span className="rounded-full bg-[#e76f51] px-1.5 py-0.5 text-[10px] font-bold text-white">{n.badge}</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}
        {children}
      </main>
    </div>
  );
};
